import React from 'react';
import {
	Checkbox,
	Chip,
	IconButton,
	MenuItem,
	TextField,
	TextFieldProps,
	useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { IndustryCategory } from 'common/services/analytics/tractionAnalytics';
import { getIndustryCategoryLabel, getIndustryCategoryOptions } from 'utils/registerUtils';
import { RiCheckboxBlankLine, RiCheckboxFill, RiCloseLine } from 'react-icons/ri';

const MENU_HEIGHT = 80;
const MENU_PADDING_TOP = 8;

interface Props extends Omit<TextFieldProps, 'value' | 'onChange'> {
	value: IndustryCategory[];
	onChange: (categories: IndustryCategory[]) => void;
}
const IndustrySelector = (props: Props) => {
	const { value, onChange, ...textFieldProps } = props;
	const classes = useStyles();
	const theme = useTheme();
	const industryCategories = getIndustryCategoryOptions();

	return (
		<TextField
			{...textFieldProps}
			select
			value={value}
			onChange={(e) => {
				const _value = e.target.value as unknown;
				return onChange(_value as IndustryCategory[]);
			}}
			fullWidth
			SelectProps={{
				multiple: true,
				MenuProps: {
					PaperProps: {
						className: classes.paper,
					},
					variant: 'menu',
				},
				renderValue: (selected) => (
					<div className={classes.chips}>
						{(selected as IndustryCategory[]).map((val) => {
							return (
								<Chip
									key={val}
									label={getIndustryCategoryLabel(val)}
									className={classes.chip}
									onDelete={() => {
										const updatedValue = value.filter((industry) => industry !== val);
										onChange(updatedValue);
									}}
									onMouseDown={(event) => {
										event.stopPropagation();
									}}
								/>
							);
						})}
					</div>
				),
			}}
			InputProps={{
				endAdornment: (
					<>
						{value.length ? (
							<IconButton
								className={classes.closeIcon}
								onClick={() => {
									onChange([]);
								}}
								size="large"
							>
								<RiCloseLine size={22} />
							</IconButton>
						) : null}
					</>
				),
			}}
		>
			{industryCategories.map((industry) => (
				<MenuItem key={industry.type} value={industry.type}>
					<Checkbox
						checked={value.indexOf(industry.type) > -1}
						icon={<RiCheckboxBlankLine color={theme.palette.colors.tundra.main} />}
						checkedIcon={<RiCheckboxFill color={theme.palette.primary.main} />}
						className={classes.checkBoxIcon}
					/>
					{industry.text}
				</MenuItem>
			))}
		</TextField>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		checkBoxIcon: {
			'&:hover': {
				backgroundColor: 'rgba(40, 1, 123, 0.3)',
			},
		},
		chips: {
			display: 'flex',
			flexWrap: 'wrap',
		},
		chip: {
			margin: 2,
		},
		closeIcon: {
			marginRight: theme.spacing(1.5),
			padding: theme.spacing(0.5),
			color: theme.palette.action.active,
		},
		paper: {
			maxHeight: MENU_HEIGHT * 4.5 + MENU_PADDING_TOP,
			width: 250,
		},
	}),
);

export default IndustrySelector;
